import { mergeAuthHeaders, getAccessToken, refreshAccessToken } from '../auth/tokenService';
import { apiUrl, resolveRequestUrl } from './config';
import { apiAxios, assertAxiosJsonOk } from './axiosClient';
import {
  shouldAttachGlobalApiLoader,
  globalApiLoadingBegin,
  globalApiLoadingEnd,
} from './apiLoading';

/** Matches server cap in `server/services/gcsImages.js`. */
export const GCP_IMAGES_MAX = 50;

const SIGNED_URL_CACHE = new Map();
const SIGNED_URL_SKEW_MS = 60 * 1000;

function parseSignedUrlExpiry(isoOrMs) {
  if (!isoOrMs) return 0;
  if (typeof isoOrMs === 'number' && Number.isFinite(isoOrMs)) return isoOrMs;
  const t = Date.parse(String(isoOrMs));
  return Number.isFinite(t) ? t : 0;
}

function getCachedSignedUrl(objectKey) {
  const key = String(objectKey || '').trim();
  if (!key) return '';
  const entry = SIGNED_URL_CACHE.get(key);
  if (!entry) return '';
  if (entry.expiresAt <= Date.now() + SIGNED_URL_SKEW_MS) {
    SIGNED_URL_CACHE.delete(key);
    return '';
  }
  return entry.url;
}

/** Seed cache from list API or library items (avoids repeat signed-read calls). */
export function seedGcpSignedUrlCache(items = []) {
  for (const item of items || []) {
    const key = String(item?.objectKey || '').trim();
    const signed = typeof item?.signedUrl === 'string' ? item.signedUrl.trim() : '';
    if (!key || !signed) continue;
    SIGNED_URL_CACHE.set(key, {
      url: signed,
      expiresAt: parseSignedUrlExpiry(item.signedUrlExpiresAt) || Date.now() + 55 * 60 * 1000,
    });
  }
}

/**
 * Fetch a fresh v4 signed GET URL for one object (authenticated JSON API; img loads GCS directly).
 * @param {string} objectKey
 */
export async function fetchGcpSignedReadUrl(objectKey) {
  const key = String(objectKey || '').trim();
  if (!key) throw new Error('Missing object key');

  const cached = getCachedSignedUrl(key);
  if (cached) return cached;

  const res = await apiAxios.get('/api/gcp-images/signed-read', {
    params: { objectKey: key },
    skipGlobalLoader: true,
  });
  const data = assertAxiosJsonOk(res);
  const signed = typeof data.signedUrl === 'string' ? data.signedUrl.trim() : '';
  if (!signed) throw new Error('Signed read URL missing from API response');
  SIGNED_URL_CACHE.set(key, {
    url: signed,
    expiresAt: parseSignedUrlExpiry(data.signedUrlExpiresAt) || Date.now() + 55 * 60 * 1000,
  });
  return signed;
}

/**
 * List images stored under the configured GCS prefix.
 * @returns {Promise<{ items: Array<{ objectKey: string, contentType: string, size: number, updated?: string, signedUrl?: string, signedUrlExpiresAt?: string }>, count: number, max: number, prefix: string }>}
 */
export async function listGcpImages() {
  const res = await apiAxios.get('/api/gcp-images');
  return assertAxiosJsonOk(res);
}

/**
 * Same-origin URL to stream a private object through the Node API (no GCS CORS).
 * @param {string} objectKey
 */
export function gcpImageStreamUrl(objectKey) {
  const key = String(objectKey || '').trim();
  if (!key) return '';
  const q = encodeURIComponent(key);
  return apiUrl(`/api/gcp-images/stream?objectKey=${q}`);
}

/**
 * Thumbnail in library grid — prefer signed GET URL (display-only, no canvas CORS).
 * @param {{ objectKey: string, signedUrl?: string }} item
 */
export function gcpImageThumbnailUrl(item) {
  if (!item?.objectKey) return '';
  const signed = typeof item.signedUrl === 'string' ? item.signedUrl.trim() : '';
  if (signed) return signed;
  return gcpImageStreamUrl(item.objectKey);
}

/**
 * Canvas / drag URL — always API stream (loaded via {@link fetchGcpImageBlobUrl}).
 * @param {{ objectKey: string }} item
 */
export function gcpImageItemUrl(item) {
  if (!item?.objectKey) return '';
  return gcpImageStreamUrl(item.objectKey);
}

export function isGcpImageStreamUrl(src) {
  return /\/api\/gcp-images\/stream(?:\?|$)/i.test(String(src || ''));
}

export function isDirectGcsUrl(src) {
  return /(^https?:\/\/)?([^/]+\.)?googleapis\.com\//i.test(String(src || ''));
}

/** Extract GCS object key from a saved stream URL (legacy templates). */
export function extractGcpObjectKeyFromSrc(src) {
  if (!isGcpImageStreamUrl(src)) return '';
  try {
    const u = new URL(String(src), typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    return normalizeGcpObjectKey(u.searchParams.get('objectKey'));
  } catch {
    return '';
  }
}

/** Extract object key from a v4 signed storage.googleapis.com URL (legacy saves without objectKey). */
export function extractGcpObjectKeyFromGcsUrl(src) {
  if (!isDirectGcsUrl(src)) return '';
  try {
    const parts = decodeURIComponent(new URL(String(src)).pathname)
      .split('/')
      .filter(Boolean);
    const marker = 'dynamic-flyer-images';
    const idx = parts.findIndex((p) => p === marker);
    if (idx !== -1) return parts.slice(idx).join('/');
    if (parts.length >= 2) return parts.slice(1).join('/');
    return '';
  } catch {
    return '';
  }
}

/** Decode objectKey from API query strings (single or double URL encoding). */
export function normalizeGcpObjectKey(raw) {
  let key = String(raw || '').trim();
  if (!key) return '';
  for (let i = 0; i < 2; i += 1) {
    try {
      const next = decodeURIComponent(key);
      if (next === key) break;
      key = next;
    } catch {
      break;
    }
  }
  return key.trim();
}

/**
 * Resolve the stable GCS object key for any image node or src string.
 * @param {{ src?: string, objectKey?: string, gcpObjectKey?: string }|string} nodeOrSrc
 * @param {string} [objectKey]
 */
export function resolveImageObjectKey(nodeOrSrc, objectKey) {
  if (nodeOrSrc && typeof nodeOrSrc === 'object') {
    const node = nodeOrSrc;
    let key = normalizeGcpObjectKey(node.objectKey || node.gcpObjectKey);
    if (key) return key;
    return resolveImageObjectKey(node.src);
  }
  let key = normalizeGcpObjectKey(objectKey);
  if (key) return key;
  const raw = String(nodeOrSrc || '').trim();
  if (!raw) return '';
  if (!/^https?:\/\//i.test(raw) && !raw.startsWith('data:') && !raw.startsWith('blob:')) {
    return normalizeGcpObjectKey(raw);
  }
  key = extractGcpObjectKeyFromSrc(raw);
  if (key) return normalizeGcpObjectKey(key);
  key = extractGcpObjectKeyFromGcsUrl(raw);
  return key ? normalizeGcpObjectKey(key) : '';
}

const EXPORT_DATA_URL_CACHE = new Map();
let tokenRefreshInFlight = null;

async function refreshAccessTokenOnce() {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');
  if (!tokenRefreshInFlight) {
    tokenRefreshInFlight = refreshAccessToken(token).finally(() => {
      tokenRefreshInFlight = null;
    });
  }
  return tokenRefreshInFlight;
}

export function clearGcpExportImageCache() {
  EXPORT_DATA_URL_CACHE.clear();
}

/** True when src must not be persisted (session-only or expires). */
export function isEphemeralImageSrc(src) {
  const s = String(src || '').trim();
  if (!s) return false;
  if (s.startsWith('blob:')) return true;
  if (isDirectGcsUrl(s)) return true;
  return false;
}

/**
 * Normalize Konva image nodes for storage/reload — keep stable objectKey + stream URL.
 * Never persist blob: or signed GCS URLs without objectKey.
 * Preserves data: URLs (used during PDF export inlining).
 */
export function normalizeKonvaImageNode(node) {
  if (!node || node.type !== 'image') return node;
  const src = String(node.src || '').trim();
  if (src.startsWith('data:')) {
    const key = resolveImageObjectKey(node);
    return {
      ...node,
      ...(key ? { objectKey: key, gcpObjectKey: key } : {}),
      src,
    };
  }
  const key = resolveImageObjectKey(node);
  if (key) {
    return {
      ...node,
      objectKey: key,
      gcpObjectKey: key,
      src: gcpImageStreamUrl(key),
    };
  }
  if (isEphemeralImageSrc(src)) {
    return { ...node, src: '' };
  }
  return node;
}

export function normalizeKonvaSceneImages(scene) {
  if (!scene || !Array.isArray(scene.objects)) return scene;
  return {
    ...scene,
    objects: scene.objects.map((o) => (o?.type === 'image' ? normalizeKonvaImageNode(o) : o)),
  };
}

export function gcpStreamApiPath(srcOrObjectKey) {
  const raw = normalizeGcpObjectKey(srcOrObjectKey);
  if (!raw) return '';
  if (isGcpImageStreamUrl(raw)) {
    try {
      const u = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      const key = normalizeGcpObjectKey(u.searchParams.get('objectKey'));
      if (key) return `/api/gcp-images/stream?objectKey=${encodeURIComponent(key)}`;
      return u.pathname + u.search;
    } catch {
      return raw.startsWith('/') ? raw : `/${raw}`;
    }
  }
  return `/api/gcp-images/stream?objectKey=${encodeURIComponent(raw)}`;
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function fetchGcpImageDataUrlViaSigned(key) {
  const cachedSigned = getCachedSignedUrl(key);
  const signed = cachedSigned || (await fetchGcpSignedReadUrl(key));
  return signedGcsUrlToDataUrl(signed);
}

/** Fetch a GCP object as a base64 data URL (primary path for PDF export). */
export async function fetchGcpImageDataUrl(objectKey) {
  const key = resolveImageObjectKey(objectKey);
  if (!key) throw new Error('Missing object key');
  if (EXPORT_DATA_URL_CACHE.has(key)) return EXPORT_DATA_URL_CACHE.get(key);

  try {
    const dataUrl = await fetchGcpImageDataUrlViaSigned(key);
    EXPORT_DATA_URL_CACHE.set(key, dataUrl);
    return dataUrl;
  } catch (signedErr) {
    console.warn('[gcp-images] signed URL data fetch failed:', key, signedErr?.message || signedErr);
  }

  const blob = await fetchGcpImageBlob(key);
  const dataUrl = await blobToDataUrl(blob);
  EXPORT_DATA_URL_CACHE.set(key, dataUrl);
  return dataUrl;
}

/**
 * Batch-fetch GCP images as data URLs (one authenticated API call; avoids parallel 401 races).
 * @param {string[]} objectKeys
 * @returns {Promise<Map<string, string>>} objectKey → dataUrl
 */
export async function batchFetchGcpImageDataUrls(objectKeys) {
  const keys = [
    ...new Set((objectKeys || []).map((k) => resolveImageObjectKey(k)).filter(Boolean)),
  ];
  const out = new Map();
  const missing = keys.filter((k) => {
    const cached = EXPORT_DATA_URL_CACHE.get(k);
    if (cached) {
      out.set(k, cached);
      return false;
    }
    return true;
  });
  if (!missing.length) return out;

  try {
    const res = await apiAxios.post(
      '/api/gcp-images/batch-data',
      { objectKeys: missing },
      { skipGlobalLoader: true },
    );
    const data = assertAxiosJsonOk(res);
    for (const item of data.items || []) {
      const key = resolveImageObjectKey(item.objectKey);
      if (key && item.dataUrl) {
        EXPORT_DATA_URL_CACHE.set(key, item.dataUrl);
        out.set(key, item.dataUrl);
      } else if (key) {
        console.warn('[gcp-images] batch-data missing image:', key, item.error || '');
      }
    }
  } catch (batchErr) {
    console.warn('[gcp-images] batch-data failed, falling back to per-image fetch:', batchErr?.message || batchErr);
  }

  const stillMissing = missing.filter((k) => !out.has(k));
  if (stillMissing.length) {
    await Promise.all(
      stillMissing.map(async (key) => {
        if (out.has(key)) return;
        try {
          const dataUrl = await fetchGcpImageDataUrl(key);
          out.set(key, dataUrl);
        } catch (err) {
          console.warn('[gcp-images] single fetch failed:', key, err?.message || err);
        }
      }),
    );
  }

  return out;
}

/**
 * Inline every image node in a scene for export (mutates nodes in place).
 * @param {object} scene
 */
export async function inlineSceneImageNodesForExport(scene) {
  if (!scene?.objects?.length) return;
  const images = scene.objects.filter((o) => o?.type === 'image');
  if (!images.length) return;

  for (const obj of images) {
    const key = resolveImageObjectKey(obj);
    if (key) {
      obj.objectKey = key;
      obj.gcpObjectKey = key;
    }
  }

  const keys = images.map((o) => resolveImageObjectKey(o)).filter(Boolean);
  const dataByKey = await batchFetchGcpImageDataUrls(keys);

  await Promise.all(
    images.map(async (obj) => {
      const src = String(obj.src || '').trim();
      if (src.startsWith('data:')) return;
      const key = resolveImageObjectKey(obj);
      const fromBatch = key ? dataByKey.get(key) : '';
      if (fromBatch) {
        obj.src = fromBatch;
        return;
      }
      const resolved = await resolveImageToDataUrl(src, key);
      if (resolved && String(resolved).startsWith('data:')) {
        obj.src = resolved;
        if (key) EXPORT_DATA_URL_CACHE.set(key, resolved);
      } else {
        console.warn('[gcp-images] export inline failed:', key || src);
      }
    }),
  );
}

/** Decode a signed GCS GET URL to a data URL (fetch or canvas fallback). */
async function signedGcsUrlToDataUrl(signedUrl) {
  const url = String(signedUrl || '').trim();
  if (!url) throw new Error('Missing signed URL');

  try {
    const resp = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (resp.ok) {
      const blob = await resp.blob();
      if (blob.size) return blobToDataUrl(blob);
    }
  } catch {
    /* try canvas decode below */
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const w = Math.max(1, img.naturalWidth || img.width || 1);
        const h = Math.max(1, img.naturalHeight || img.height || 1);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas not supported');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Could not decode signed image URL'));
    img.src = url;
  });
}

function isImageResponseBlob(blob, contentTypeHeader) {
  const ct = String(contentTypeHeader || blob?.type || '').toLowerCase();
  if (!ct) return true;
  if (ct.includes('text/html') || ct.includes('application/json')) return false;
  return ct.startsWith('image/') || ct === 'application/octet-stream';
}

function streamFetchCredentials(url) {
  try {
    if (typeof window === 'undefined') return 'same-origin';
    return new URL(url, window.location.href).origin !== window.location.origin
      ? 'include'
      : 'same-origin';
  } catch {
    return 'same-origin';
  }
}

/** Fetch image bytes through authenticated flyer API (validates response is actually an image). */
export async function fetchGcpImageBlob(objectKeyOrStreamSrc, allowRetry = true) {
  const path = gcpStreamApiPath(objectKeyOrStreamSrc);
  if (!path) throw new Error('Missing object key');

  const url = resolveRequestUrl(path);
  const sep = url.includes('?') ? '&' : '?';
  const headers = mergeAuthHeaders({
    Accept: 'image/*,*/*',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  });

  const res = await fetch(`${url}${sep}_=${Date.now()}`, {
    method: 'GET',
    headers,
    credentials: streamFetchCredentials(url),
    cache: 'no-store',
  });

  if (res.status === 401 && allowRetry && getAccessToken()) {
    try {
      await refreshAccessTokenOnce();
    } catch {
      /* fall through to error below */
    }
    return fetchGcpImageBlob(objectKeyOrStreamSrc, false);
  }

  if (res.status < 200 || res.status >= 300) {
    throw new Error(`Could not load image (HTTP ${res.status})`);
  }

  const blob = await res.blob();
  if (!(blob instanceof Blob) || !blob.size) {
    throw new Error('Could not load image (empty response)');
  }
  if (!isImageResponseBlob(blob, res.headers.get('content-type'))) {
    throw new Error('Could not load image (API returned non-image response)');
  }
  return blob;
}

/**
 * Resolve any image src (GCP stream, signed URL, blob) to a data URL for PDF/html2canvas export.
 */
export async function resolveImageToDataUrl(src, objectKey) {
  const raw = String(src || '').trim();
  if (!raw || raw.startsWith('data:')) return raw;

  let key = resolveImageObjectKey(raw, objectKey);

  if (key) {
    const cached = EXPORT_DATA_URL_CACHE.get(key);
    if (cached) return cached;
    try {
      const dataUrl = await fetchGcpImageDataUrlViaSigned(key);
      EXPORT_DATA_URL_CACHE.set(key, dataUrl);
      return dataUrl;
    } catch (signedErr) {
      console.warn('[gcp-images] PDF signed URL decode failed:', key, signedErr?.message || signedErr);
    }
  }

  if (key || isGcpImageStreamUrl(raw)) {
    try {
      const blob = await fetchGcpImageBlob(key || raw);
      const dataUrl = await blobToDataUrl(blob);
      if (key) EXPORT_DATA_URL_CACHE.set(key, dataUrl);
      return dataUrl;
    } catch (streamErr) {
      console.warn('[gcp-images] PDF stream fetch failed:', key || raw, streamErr?.message || streamErr);
    }
  }

  if (raw.startsWith('blob:')) {
    try {
      const resp = await fetch(raw);
      if (resp.ok) {
        const blob = await resp.blob();
        if (blob.size) return blobToDataUrl(blob);
      }
    } catch {
      /* fall through */
    }
  }

  if (/^https?:\/\//i.test(raw)) {
    try {
      const blobUrl = await fetchUrlAsBlobUrl(raw);
      const resp = await fetch(blobUrl);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const dataUrl = await blobToDataUrl(await resp.blob());
      if (blobUrl.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(blobUrl);
        } catch {
          /* ignore */
        }
      }
      return dataUrl;
    } catch (err) {
      console.warn('[gcp-images] external URL export failed:', err?.message || err);
    }
  }

  return raw;
}

/**
 * Load image bytes through the flyer API (auth + correct host) as a `blob:` URL.
 * @param {string} objectKeyOrStreamSrc
 */
export async function fetchGcpImageBlobUrl(objectKeyOrStreamSrc) {
  const blob = await fetchGcpImageBlob(objectKeyOrStreamSrc);
  return URL.createObjectURL(blob);
}

/** Decode image bytes via fetch (avoids tainted canvas from cross-origin GCS signed URLs). */
async function fetchUrlAsBlobUrl(url) {
  const src = String(url || '').trim();
  if (!src) throw new Error('Missing image URL');
  if (src.startsWith('blob:') || src.startsWith('data:')) return src;

  const res = await fetch(src, { mode: 'cors', credentials: 'omit' });
  if (!res.ok) throw new Error(`Could not load image (HTTP ${res.status})`);
  const blob = await res.blob();
  if (!blob.size) throw new Error('Could not load image (empty response)');
  return URL.createObjectURL(blob);
}

/** Decode a same-origin image URL via canvas (never use for cross-origin GCS URLs). */
function sameOriginImageUrlToBlobUrl(url) {
  return new Promise((resolve, reject) => {
    const src = String(url || '').trim();
    if (!src) {
      reject(new Error('Missing image URL'));
      return;
    }
    let sameOrigin = false;
    try {
      sameOrigin = new URL(src, window.location.href).origin === window.location.origin;
    } catch {
      sameOrigin = false;
    }
    if (!sameOrigin) {
      reject(new Error('Cross-origin image cannot be decoded via canvas'));
      return;
    }
    const img = new Image();
    img.onload = () => {
      const w = Math.max(1, img.naturalWidth || img.width || 1);
      const h = Math.max(1, img.naturalHeight || img.height || 1);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => {
          if (!blob) reject(new Error('Could not decode image'));
          else resolve(URL.createObjectURL(blob));
        },
        'image/png',
      );
    };
    img.onerror = () => reject(new Error('Could not load image'));
    img.src = src;
  });
}

/** @deprecated Prefer {@link fetchGcpImageBlobUrl} or {@link fetchUrlAsBlobUrl}. */
export function imageUrlToBlobUrl(url) {
  return fetchUrlAsBlobUrl(url).catch(() => sameOriginImageUrlToBlobUrl(url));
}

/**
 * Best URL for Konva/img display — signed GCS GET when possible (no auth-gated stream proxy).
 * Falls back to authenticated API stream as blob: URL.
 * @param {string} [src]
 * @param {string} [objectKey]
 */
export async function resolveGcpImageForCanvas(src, objectKey) {
  const srcStr = String(src || '').trim();
  if (!srcStr && !objectKey) return srcStr;
  if (srcStr.startsWith('data:') || srcStr.startsWith('blob:')) return srcStr;

  let key = String(objectKey || '').trim();
  if (!key) key = extractGcpObjectKeyFromSrc(srcStr);

  if (key) {
    const cached = getCachedSignedUrl(key);
    if (cached) return cached;
    try {
      return await fetchGcpSignedReadUrl(key);
    } catch (err) {
      console.warn('[gcp-images] signed-read failed, trying stream:', key, err?.message || err);
    }
  }

  if (isDirectGcsUrl(srcStr)) return srcStr;

  if (isGcpImageStreamUrl(srcStr) || key) {
    try {
      return await fetchGcpImageBlobUrl(key || srcStr);
    } catch {
      /* fall through */
    }
  }

  return srcStr;
}

/**
 * Canvas/library click — prefer signed GCS URL (same as thumbnails; no stream 401).
 * @param {{ objectKey: string, signedUrl?: string }} item
 * @returns {Promise<string>} URL safe for Konva `Image` / `<img>` `.src`
 */
export async function loadGcpImageForCanvas(item) {
  if (!item?.objectKey) throw new Error('Missing object key');
  const signed = typeof item.signedUrl === 'string' ? item.signedUrl.trim() : '';
  if (signed) {
    SIGNED_URL_CACHE.set(item.objectKey, {
      url: signed,
      expiresAt: parseSignedUrlExpiry(item.signedUrlExpiresAt) || Date.now() + 55 * 60 * 1000,
    });
    return signed;
  }
  return resolveGcpImageForCanvas(gcpImageStreamUrl(item.objectKey), item.objectKey);
}

/** Whether Konva/img can load this URL without setting crossOrigin (GCS signed GET). */
export function isGcpDisplayOnlyUrl(src) {
  const s = String(src || '');
  return s.startsWith('data:') || s.startsWith('blob:') || isDirectGcsUrl(s);
}

/**
 * Request a v4 signed POST policy (optional: direct browser POST to GCS; requires bucket CORS).
 */
export async function requestGcpSignedUpload(contentType, originalName = '') {
  const res = await apiAxios.post('/api/gcp-images/signed-upload', {
    contentType,
    originalName,
  });
  return assertAxiosJsonOk(res);
}

/**
 * POST file to GCS (multipart). Prefer {@link uploadGcpImageFile} unless bucket CORS is configured.
 */
export async function uploadBytesToGcpSignedUrl(uploadUrl, uploadFields, file) {
  const form = new FormData();
  Object.entries(uploadFields).forEach(([k, v]) => {
    form.append(k, v);
  });
  form.append('file', file);
  const trackLoader = shouldAttachGlobalApiLoader(String(uploadUrl || ''));
  if (trackLoader) globalApiLoadingBegin();
  try {
    const r = await fetch(uploadUrl, {
      method: 'POST',
      body: form,
      mode: 'cors',
    });
    if (!r.ok) {
      const t = await r.text().catch(() => '');
      throw new Error(t || `Upload failed (${r.status})`);
    }
  } finally {
    if (trackLoader) globalApiLoadingEnd();
  }
}

/**
 * Upload via flyer API (server writes to GCS). Avoids browser → googleapis.com CORS failures.
 */
export async function uploadGcpImageFile(file) {
  const contentType = String(file.type || 'image/png')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase()
    .replace('image/jpg', 'image/jpeg');
  if (!/^image\/(png|jpeg|gif|webp|svg\+xml)$/.test(contentType)) {
    throw new Error('File must be PNG, JPEG, GIF, WebP, or SVG.');
  }
  const url = apiUrl('/api/gcp-images/upload');
  let crossOrigin = false;
  try {
    if (typeof window !== 'undefined') {
      crossOrigin = new URL(url, window.location.href).origin !== window.location.origin;
    }
  } catch {
    crossOrigin = false;
  }
  const headers = mergeAuthHeaders({
    'Content-Type': contentType,
    'X-Original-Name': encodeURIComponent(file.name || ''),
    Accept: 'application/json',
  });
  const trackLoader = shouldAttachGlobalApiLoader(url);
  if (trackLoader) globalApiLoadingBegin();
  let r;
  try {
    r = await fetch(url, {
      method: 'POST',
      headers,
      body: file,
      credentials: crossOrigin ? 'include' : 'same-origin',
    });
  } finally {
    if (trackLoader) globalApiLoadingEnd();
  }
  const text = await r.text().catch(() => '');
  let data = {};
  try {
    if (text) data = JSON.parse(text);
  } catch {
    /* ignore */
  }
  if (!r.ok) {
    throw new Error((data && data.error) || text || `Upload failed (${r.status})`);
  }
  if (!data.objectKey) {
    throw new Error('Upload response missing objectKey');
  }
  return { objectKey: data.objectKey };
}

/**
 * Delete one object (server checks key is under app prefix).
 * @param {string} objectKey
 */
export async function removeGcpImage(objectKey) {
  const res = await apiAxios.post('/api/gcp-images/remove', { objectKey });
  return assertAxiosJsonOk(res);
}
